import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { Account, Tenant } from 'src/models/account.model';
import { User } from 'src/models/user.model';
import { AuthService } from 'src/services/auth.service';
import { TenantService } from 'src/services/tenant.service';
import { VoucherService } from 'src/services/voucher.service';
import Swal from 'sweetalert2';

interface VoucherStats {
  total: number;
  active: number;
  used: number;
  expired: number;
  loading: boolean;
}

interface PreAuthSession {
  token: string;
  tenant_id: string;
  username: string;
  pin: string;
  account_key: string;
  created_at: Date;
  expires_at: Date;
}

interface TenantPerformance {
  tenant: Tenant;
  voucherCount: number;
  rank: number;
  badge?: 'gold' | 'silver' | 'bronze';
}

interface TenantAnalytics {
  totalTenants: number;
  activeTenants: number;
  totalVouchers: number;
  vouchersToday: number;
  vouchersThisWeek: number;
  vouchersThisMonth: number;
  activeVouchers: number;
  usedVouchers: number;
  expiredVouchers: number;
  redemptionRate: number;
  topPerformersToday: TenantPerformance[];
  topPerformersWeek: TenantPerformance[];
  topPerformersMonth: TenantPerformance[];
  topPerformersAllTime: TenantPerformance[];
}

@Component({
  selector: 'app-tenant-list',
  templateUrl: './tenant-list.component.html',
  styleUrls: ['./tenant-list.component.css']
})
export class TenantListComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  // Cache for voucher stats per tenant
  voucherStatsMap: Map<string, VoucherStats> = new Map();

  get color(): string {
    return this._color;
  }
  set color(color: string) {
    this._color = color !== 'light' && color !== 'dark' ? 'light' : color;
  }

  get paginatedTenants(): Tenant[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredTenants.slice(startIndex, endIndex);
  }

  private _color = 'light';

  @Input() filterTenant: string = '';

  tenants: Tenant[] = [];
  filteredTenants: Tenant[] = [];
  userAuth: User;
  account: Account;

  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  // Analytics
  analytics: TenantAnalytics = {
    totalTenants: 0,
    activeTenants: 0,
    totalVouchers: 0,
    vouchersToday: 0,
    vouchersThisWeek: 0,
    vouchersThisMonth: 0,
    activeVouchers: 0,
    usedVouchers: 0,
    expiredVouchers: 0,
    redemptionRate: 0,
    topPerformersToday: [],
    topPerformersWeek: [],
    topPerformersMonth: [],
    topPerformersAllTime: []
  };
  analyticsLoading = false;

  constructor(
    private tenantService: TenantService,
    private voucherService: VoucherService,
    private route: ActivatedRoute,
    protected router: Router,
    public auth: AuthService
  ) { }

  async ngOnInit() {
    // Subscribe to route params to get account_key - this is the source of truth
    this.route.parent?.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(async params => {
        const accountKey = params['account_key'];
        if (accountKey) {
          await this.loadAccountByKey(accountKey);
        }
      });

    // Subscribe to user changes
    this.auth.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.userAuth = user;
      });
  }

  /**
   * Load account data by account_key from Firestore
   * This ensures we always have fresh data based on the URL
   */
  private async loadAccountByKey(accountKey: string): Promise<void> {
    try {
      const freshAccount = await this.auth.getAccountByKey(accountKey);
      if (freshAccount) {
        this.account = freshAccount;
        // Also update localStorage to keep it in sync
        localStorage.setItem('account', JSON.stringify(freshAccount));
        // Clear cached voucher stats when account changes
        this.voucherStatsMap.clear();
        this.loadTenants();
      } else {
        console.error('Account not found for key:', accountKey);
        this.account = null;
        this.tenants = [];
      }
    } catch (error) {
      console.error('Error loading account by key:', error);
      this.account = null;
      this.tenants = [];
    }
  }

  /**
   * Refresh account data from Firestore
   */
  async refreshAccountData(): Promise<void> {
    if (this.account?.account_key) {
      await this.loadAccountByKey(this.account.account_key);
    }
  }

  loadTenants() {
    if (this.account?.settings?.tenants) {
      // Sort tenants by date_created descending
      this.tenants = [...this.account.settings.tenants].sort((a, b) => {
        const dateA = a.date_created instanceof Date
          ? a.date_created
          : new Date((a.date_created as any)?.seconds * 1000 || 0);
        const dateB = b.date_created instanceof Date
          ? b.date_created
          : new Date((b.date_created as any)?.seconds * 1000 || 0);
        return dateB.getTime() - dateA.getTime();
      });
    } else {
      this.tenants = [];
    }
    this.applyPagination();

    // Calculate analytics after tenants are loaded
    // Use setTimeout to allow component to initialize
    setTimeout(() => {
      this.calculateAnalytics();
    }, 500);
  }

  async addTenant() {
    if (!this.auth.isSuperAdmin(this.userAuth)) {
      await Swal.fire({
        title: 'Ooops!',
        html: 'You have no rights to add tenant. Please contact your admin.',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
      return;
    }

    this.router.navigate([this.account.account_key + '/user/tenants/add']);
  }

  async editTenant(tenant: Tenant) {
    if (!this.auth.isAdminCanEdit(this.userAuth)) {
      await Swal.fire({
        title: 'Ooops!',
        html: 'You have no rights to edit tenant. Please contact your admin.',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
      return;
    }

    this.router.navigate([this.account.account_key + '/user/tenants/edit/', tenant.tenant_id]);
  }

  async deleteTenant(tenant: Tenant) {
    if (!this.auth.isSuperAdmin(this.userAuth)) {
      await Swal.fire({
        title: 'Ooops!',
        html: 'You have no rights to delete tenant. Please contact your admin.',
        icon: 'warning',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete tenant "${tenant.store_name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it'
    });

    if (result.isConfirmed) {
      await Swal.fire({
        title: 'Deleting tenant...',
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: async () => {
          Swal.showLoading();
          try {
            await this.tenantService.deleteTenant(this.account, tenant.tenant_id);

            // Refresh account data from Firestore to get updated tenant list
            await this.refreshAccountData();

            Swal.fire('Deleted!', 'Tenant has been deleted.', 'success');
          } catch (error) {
            Swal.fire('Error!', 'Failed to delete tenant.', 'error');
          }
        }
      });
    }
  }

  getVouchersGenerated(tenant: Tenant): number {
    return tenant.voucher_settings?.vouchers_generated || 0;
  }

  getVoucherLimit(tenant: Tenant): string {
    if (tenant.voucher_settings?.generation_limit === null ||
      tenant.voucher_settings?.generation_limit === undefined) {
      return 'Unlimited';
    }
    return tenant.voucher_settings.generation_limit.toString();
  }

  getTenantPortalUrl(tenant: Tenant): string {
    return `/tenant/${this.account.account_key}/login`;
  }

  /**
   * Get voucher stats for a tenant - returns cached value or loading state
   */
  getVoucherStats(tenantId: string): VoucherStats {
    if (!this.voucherStatsMap.has(tenantId)) {
      // Initialize with loading state and trigger fetch
      this.voucherStatsMap.set(tenantId, {
        total: 0,
        active: 0,
        used: 0,
        expired: 0,
        loading: true
      });
      this.loadVoucherStatsForTenant(tenantId);
    }
    return this.voucherStatsMap.get(tenantId);
  }

  /**
   * Load voucher stats for a specific tenant from Firestore
   */
  private loadVoucherStatsForTenant(tenantId: string): void {
    this.voucherService.getVouchersByTenant(tenantId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (vouchers) => {
          const now = new Date();
          const stats: VoucherStats = {
            total: vouchers.length,
            active: 0,
            used: 0,
            expired: 0,
            loading: false
          };

          vouchers.forEach(voucher => {
            const expiresAt = voucher.expires_at instanceof Date
              ? voucher.expires_at
              : new Date((voucher.expires_at as any)?.seconds * 1000 || 0);

            if (voucher.status === 'used') {
              stats.used++;
            } else if (voucher.status === 'expired' || now > expiresAt) {
              stats.expired++;
            } else {
              stats.active++;
            }
          });

          this.voucherStatsMap.set(tenantId, stats);
        },
        error: (error) => {
          console.error(`Error loading voucher stats for tenant ${tenantId}:`, error);
          this.voucherStatsMap.set(tenantId, {
            total: 0,
            active: 0,
            used: 0,
            expired: 0,
            loading: false
          });
        }
      });
  }

  /**
   * Refresh voucher stats for all tenants
   */
  refreshAllVoucherStats(): void {
    this.voucherStatsMap.clear();
    this.tenants.forEach(tenant => {
      if (tenant.tenant_id) {
        this.getVoucherStats(tenant.tenant_id);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  copyTenantPortalUrl() {
    if (!this.account?.account_key) {
      Swal.fire({
        title: 'Error',
        text: 'Account key not found. Please refresh the page.',
        icon: 'error'
      });
      return;
    }

    const url = window.location.origin + `/tenant/${this.account.account_key}/login`;

    navigator.clipboard.writeText(url).then(() => {
      Swal.fire({
        title: 'Copied!',
        html: `
          <div style="text-align: left;">
            <p><strong>Portal URL:</strong></p>
            <p class="text-sm text-gray-600"><a href="${url}" target="_blank" style="color: #3b82f6; text-decoration: underline;">${url}</a></p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'OK'
      });
    }).catch(() => {
      Swal.fire({
        title: 'Copy Failed',
        html: `
          <div style="text-align: left;">
            <p>Please copy manually:</p>
            <p class="text-sm text-gray-600 mt-2"><a href="${url}" target="_blank" style="color: #3b82f6; text-decoration: underline;">${url}</a></p>
          </div>
        `,
        icon: 'info'
      });
    });
  }

  applyPagination(): void {
    this.filteredTenants = [...this.tenants];
    this.totalItems = this.filteredTenants.length;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.getTotalPages()) {
      this.currentPage = page;
    }
  }

  getTotalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const pages = [];
    const maxPages = 5;

    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(totalPages, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  getPaginationEndIndex(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }

  /**
   * Generate a unique one-time token (UUID v4 format)
   */
  private generateOneTimeToken(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Visit tenant portal with pre-authentication
   * Generates a one-time token and opens portal in new tab
   */
  async visitPortalWithPreLogin(tenant: Tenant) {
    // Permission check - only admins can use this feature
    if (!this.auth.isAdminCanEdit(this.userAuth)) {
      await Swal.fire({
        title: 'Access Denied',
        html: 'You do not have permission to access tenant portals. Please contact your administrator.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate tenant is active
    if (!tenant.is_active) {
      await Swal.fire({
        title: 'Inactive Tenant',
        html: 'This tenant is currently inactive. Please activate the tenant first.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate required credentials exist
    if (!tenant.username || !tenant.password_pin) {
      await Swal.fire({
        title: 'Missing Credentials',
        html: 'This tenant is missing login credentials. Please update the tenant settings.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Generate one-time token
    const token = this.generateOneTimeToken();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days expiration

    // Create pre-auth session
    const preAuthSession: PreAuthSession = {
      token: token,
      tenant_id: tenant.tenant_id,
      username: tenant.username,
      pin: tenant.password_pin,
      account_key: this.account.account_key,
      created_at: now,
      expires_at: expiresAt
    };

    // Store in sessionStorage (will clear when tab closes)
    sessionStorage.setItem(`preauth_${token}`, JSON.stringify(preAuthSession));

    // Open portal in new tab with token parameter
    const portalUrl = `${window.location.origin}/tenant/${this.account.account_key}/login?preauth_token=${token}`;
    window.open(portalUrl, '_blank');

    // Show confirmation
    await Swal.fire({
      title: 'Portal Opened',
      html: `
        <div style="text-align: left;">
          <p>Opening portal for <strong>${tenant.store_name}</strong> in a new tab...</p>
          <p class="text-sm text-gray-600 mt-2">The portal will automatically log you in.</p>
        </div>
      `,
      icon: 'success',
      timer: 3000,
      showConfirmButton: false
    });
  }

  /**
   * Calculate comprehensive analytics from tenant and voucher data
   */
  async calculateAnalytics(): Promise<void> {
    // Skip if no tenants or account
    if (!this.tenants || this.tenants.length === 0 || !this.account) {
      console.log('Analytics: No tenants or account, skipping');
      this.analyticsLoading = false;
      return;
    }

    console.log('Analytics: Starting calculation for', this.tenants.length, 'tenants');
    this.analyticsLoading = true;

    try {
      // Basic tenant stats
      this.analytics.totalTenants = this.tenants.length;
      this.analytics.activeTenants = this.tenants.filter(t => t.is_active).length;

      // Fetch all vouchers directly for accurate time-based analytics
      const voucherPromises = this.tenants
        .filter(t => t?.tenant_id)
        .map(tenant =>
          this.voucherService.getVouchersByTenant(tenant.tenant_id)
            .pipe(take(1))
            .toPromise()
            .catch(err => {
              console.error(`Error fetching vouchers for tenant ${tenant.tenant_id}:`, err);
              return [];
            })
        );

      const allVoucherArrays = await Promise.all(voucherPromises);
      const allVouchers = ([] as any[]).concat(...allVoucherArrays);

      console.log('Analytics: Loaded', allVouchers.length, 'total vouchers');

      // Calculate voucher stats
      const now = new Date();
      let totalVouchers = 0;
      let activeVouchers = 0;
      let usedVouchers = 0;
      let expiredVouchers = 0;
      let vouchersToday = 0;
      let vouchersThisWeek = 0;
      let vouchersThisMonth = 0;

      // Time ranges
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);

      // Process all vouchers in one loop
      allVouchers.forEach(voucher => {
        if (!voucher) return;

        totalVouchers++;

        // Status counting
        const expiresAt = voucher.expires_at instanceof Date
          ? voucher.expires_at
          : new Date((voucher.expires_at as any)?.seconds * 1000 || 0);

        if (voucher.status === 'used') {
          usedVouchers++;
        } else if (voucher.status === 'expired' || now > expiresAt) {
          expiredVouchers++;
        } else {
          activeVouchers++;
        }

        // Time-based counting
        if (voucher.generated_at) {
          const generatedAt = voucher.generated_at instanceof Date
            ? voucher.generated_at
            : new Date((voucher.generated_at as any)?.seconds * 1000 || 0);

          if (generatedAt >= todayStart) vouchersToday++;
          if (generatedAt >= weekStart) vouchersThisWeek++;
          if (generatedAt >= monthStart) vouchersThisMonth++;
        }
      });

      // Set analytics
      this.analytics.totalVouchers = totalVouchers;
      this.analytics.activeVouchers = activeVouchers;
      this.analytics.usedVouchers = usedVouchers;
      this.analytics.expiredVouchers = expiredVouchers;
      this.analytics.vouchersToday = vouchersToday;
      this.analytics.vouchersThisWeek = vouchersThisWeek;
      this.analytics.vouchersThisMonth = vouchersThisMonth;

      // Calculate redemption rate
      this.analytics.redemptionRate = totalVouchers > 0
        ? Math.round((usedVouchers / totalVouchers) * 100)
        : 0;

      console.log('Analytics: Calculated stats -', {
        total: totalVouchers,
        today: vouchersToday,
        week: vouchersThisWeek,
        month: vouchersThisMonth
      });

      // Generate leaderboards (these use the same voucher data)
      this.analytics.topPerformersToday = await this.getTopPerformers('today', 3);
      this.analytics.topPerformersWeek = await this.getTopPerformers('week', 3);
      this.analytics.topPerformersMonth = await this.getTopPerformers('month', 3);
      this.analytics.topPerformersAllTime = await this.getTopPerformers('all', 5);

      console.log('Analytics: Calculation complete');

    } catch (error) {
      console.error('Error calculating analytics:', error);
    } finally {
      this.analyticsLoading = false;
    }
  }

  /**
   * Get top performing tenants for a given period
   */
  private async getTopPerformers(
    period: 'today' | 'week' | 'month' | 'all',
    limit: number
  ): Promise<TenantPerformance[]> {
    const now = new Date();
    let startDate: Date | null = null;

    // Determine time range
    switch (period) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        break;
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - now.getDay());
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        break;
      case 'all':
        startDate = null; // No filter
        break;
    }

    // Get voucher counts for each tenant
    const tenantCounts: { tenant: Tenant; count: number }[] = [];

    for (const tenant of this.tenants) {
      const vouchers = await this.voucherService.getVouchersByTenant(tenant.tenant_id)
        .pipe(take(1))
        .toPromise()
        .catch(() => []); let count = 0;
      if (startDate) {
        count = vouchers.filter(v => {
          const generatedAt = v.generated_at instanceof Date
            ? v.generated_at
            : new Date((v.generated_at as any)?.seconds * 1000 || 0);
          return generatedAt >= startDate!;
        }).length;
      } else {
        count = vouchers.length;
      }

      tenantCounts.push({ tenant, count });
    }

    // Sort by count descending and take top N
    const sorted = tenantCounts
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);

    // Add ranks and badges
    const performances: TenantPerformance[] = sorted.map((item, index) => {
      let badge: 'gold' | 'silver' | 'bronze' | undefined;
      if (index === 0) badge = 'gold';
      else if (index === 1) badge = 'silver';
      else if (index === 2) badge = 'bronze';

      return {
        tenant: item.tenant,
        voucherCount: item.count,
        rank: index + 1,
        badge
      };
    });

    return performances;
  }

  /**
   * Refresh analytics when voucher data changes
   */
  async refreshAnalytics(): Promise<void> {
    await this.calculateAnalytics();
  }
}
